import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { PulseLoader } from "react-spinners";
import useFetch from "../../hooks/useFetch";

import { library } from "@fortawesome/fontawesome-svg-core";
import { faSave, faCheck } from "@fortawesome/free-solid-svg-icons";
import request from "../../utils/fetch";
import toast from "react-hot-toast";
import { validateWater } from "./useReportValidation";
import MeterReportItem from "./MeterReportItem";
import { areMetersEqual } from "./utils/checkEquality";
import SectionFooter from "./SectionFooter";
import toISOString from "./utils/toISOString";
import ProblemIndicator from "./components/ProblemIndicator";

library.add(faSave, faCheck);

const MeterReport = forwardRef(({
  tractId,
  data,
  reportId,
  refreshData,
  advanceLabel,
  advanceTab,
  initialProblems,
}, ref) => {
  const [meters, setMeters] = useState(undefined);
  // Used to track which meters have unsaved changes.
  const dirtyMeters = useRef({});

  const { data: existingReports } = useFetch(`/reports/${reportId}/flowmeter`);
  const { data: metersData } = useFetch(`/tracts/${tractId}/meters`);

  const [formProblems, setFormProblems] = useState(initialProblems);

  // 'clean', 'saved', 'dirty', 'saving'.
  const [saveStatus, setSaveStatus] = useState('clean');

  useEffect(() => {
    if (metersData) {
      setMeters(metersData.meters?.reduce((acc, item) => {
        const existingEntry = (data || []).find((i) => i.Id === item._id);
        acc.push({
          label: item.alias || item.Serial_num || item.FM_ID,
          id: item._id,
          // NOTE: I don't understand checking for '0001', but it was in the og version
          //       and I don't want to risk breaking anything by not including it here.
          date: (!existingEntry?.Date || existingEntry?.Date?.includes("0001"))
            ? new Date() : new Date(existingEntry?.Date),
          reading: existingEntry?.Reading ?? '',
          note: existingEntry?.Note ?? '',
          name: item.FM_ID,
        });
        return acc;
      }, []) ?? []);
    }
  }, [data, metersData]);

  const checkDirty = useCallback((changed) => {
    if (!meters) return;

    const original = meters.find((m) => m.id === changed.id);

    if (areMetersEqual(original, changed)) {
      delete dirtyMeters.current[changed.id];
    } else {
      dirtyMeters.current[changed.id] = true;
    }

    setSaveStatus(Object.keys(dirtyMeters.current).length === 0 ? 'clean' : 'dirty');
  }, [meters]);

  useImperativeHandle(ref, () => ({
    isDirty: saveStatus === 'dirty',
  }), [saveStatus]);

  const handleSubmit = useCallback(() => {
    setSaveStatus('saving');

    // Iterate over all changed meters.
    const data = Object.keys(dirtyMeters.current).map((id) => {
      const exists = existingReports.some((m) => m.Id === id);

      const meter = meters.find((m) => m.id === id);
      const date = document.querySelector(`input[name='${id}-date']`)?.valueAsDate;
      const data = {
        Flowmeter_Name: meter.name,
        Id: meter.id,
        Note: document.querySelector(`textarea[name='${id}-note']`)?.innerHTML,
        Reading: document.querySelector(`input[name='${id}-reading']`)?.value,
        // null if date is null, otherwise iso string of the date.
        Date: date && toISOString(new Date(date)),
      };

      return { exists, data };
    });

    const problems = validateWater(data.map((d) => d.data));

    // FIXME: Should we also not submit incomplete? I think incomplete should save
    //        but not allow final submit, but that might require endpoint changes.
    const stopSubmission = problems.some((v) => v.level === 'error');

    setFormProblems(problems);

    if (stopSubmission) {
      setSaveStatus('dirty');
      return;
    }

    const promises = data.map((d) => {
      return request({
        method: d.exists ? "PATCH" : "POST",
        data: d.data,
        url: d.exists
          ? `/reports/${reportId}/flowmeter/${d.data.Id}`
          : `/reports/${reportId}/flowmeter/`,
      }).then(() => {
        if (!d.exists) {
          existingReports.push(d.data);
        }
      });
    });

    // Once all of the promises resolve, set the saveStatus appropriately.
    Promise.all(promises).then(() => {
      toast.success("Successfully updated meter report");
      refreshData().then(() => setSaveStatus("saved"));
    }).catch((e) => {
      toast.error(e.response?.data?.message ?? e.message ?? "Something went wrong");
      refreshData().then(() => setSaveStatus("dirty"));
    });
  }, [meters, existingReports]);

  return (<>
    <h3 className="text-2xl font-semibold">Flowmeters</h3>
    <div className="w-full">
      {!meters && <div className="flex p-16 self-center justify-center">
        <PulseLoader color="#658245" />
      </div>}
      {meters?.length === 0 && <span className="flex p-16 self-center justify-center">
        No flowmeters associated with this tract
      </span>}
      <br />
      {meters?.map((meter) => {
        return <MeterReportItem
          key={`meter-item-${meter.id}}`}
          formProblems={formProblems}
          name={meter.name}
          date={meter.date}
          id={meter.id}
          note={meter.note}
          reading={meter.reading}
          label={meter.label}
          checkDirty={checkDirty}
        />;
      })}
      {formProblems.filter((p) => !p.field).map((p, i) => {
        return (
          <ProblemIndicator
            key={`crop_form_issue_${i}`}
            level={p.displayLevel ?? p.level}
            message={p.message}
          />
        );
      })}
    </div>
    <SectionFooter
      handleSubmit={handleSubmit}
      advanceTab={advanceTab}
      advanceLabel={advanceLabel}
      status={saveStatus}
    />
  </>);
});

export default MeterReport;
